/* eslint-disable no-console */
import React, { useRef, useState } from 'react';
import { Box, Stack, Typography, Switch, FormControlLabel, Alert } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import { GridColDef } from '@mui/x-data-grid-premium';
import TabPanel from '@mui/lab/TabPanel';
import TabList from '@mui/lab/TabList';
import Tab from '@mui/material/Tab';
import { useSelector } from 'react-redux';
import { useSearchParamsState } from '../../hooks';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { Table, TableRef } from '../../components/Table/Table';
import { fetchUserIndexFundReport } from '../../redux/modules/report/report.actions';

const mapTabParamToValue: { [prop: string]: string } = {
  mutualFund: '1',
  etf: '2',
  default: '1',
};

const mapTabValueToTabParam: { [prop: string]: string } = {
  1: 'mutualFund',
  2: 'etf',
};

const UserIndexFundTab = () => {
  const { userIndexFundReport, fetchUserIndexFundReportStatus, totalUserIndexFundReport, error } =
    useSelector(reportSelector);
  const isLoading = fetchUserIndexFundReportStatus === fetchStatuses.pending;
  const tableRef = useRef<TableRef>(null);

  const [selectedTab, setSelectedTab] = useSearchParamsState('userIndexFundTab', 'default');
  const [groupByUser, setGroupByUser] = useState(false);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(mapTabValueToTabParam[newValue]);
    // Reset filters immediately when tab changes
    setGroupByUser(false);
  };
  const columns: GridColDef[] = [
    {
      field: 'index fund id',
      headerName: 'Index fund id',
      width: 150,
    },
    {
      field: 'index fund name',
      headerName: 'Index fund name',
      width: 350,
    },
    {
      field: 'total units',
      headerName: 'Total units',
      width: 150,
      valueFormatter: (params) => (+params.value)?.toFixed(2) || 'N/A',
    },
    ...(groupByUser
      ? [
          {
            field: 'user id',
            headerName: 'User ID',
            width: 150,
            valueFormatter: (params: any) => params.value || 'N/A',
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          control={<Switch checked={groupByUser} onChange={(e) => setGroupByUser(e.target.checked)} size="small" />}
          label="Group by User"
        />
      </Stack>
      <TabContext value={mapTabParamToValue[selectedTab]}>
        <Box>
          <TabList
            value={mapTabParamToValue[selectedTab]}
            onChange={handleChange}
            variant="standard"
            aria-label="User Index Fund tabs"
          >
            <Tab label="Mutual Fund" value="1" sx={{ textTransform: 'capitalize' }} disableRipple />
            <Tab label="ETF" value="2" sx={{ textTransform: 'capitalize' }} disableRipple />
          </TabList>
        </Box>
        <TabPanel value="1">
          <Table
            key={`mutual-fund-${groupByUser}`}
            ref={tableRef}
            columns={columns}
            fetchItems={fetchUserIndexFundReport}
            fetchItemsCustomParams={{
              category: 'Mutual Fund',
              groupByUser,
            }}
            rows={userIndexFundReport}
            loading={isLoading}
            rowCount={totalUserIndexFundReport}
            getRowId={(row) => `${row['index fund id']}-${row['user id'] || 'all'}`}
            disableColumnFilter
            toolbarExportProps={{
              csvOptions: {
                fileName: `User_Index_Fund_Report_Mutual_Fund${groupByUser ? '_Grouped_By_User' : ''}_${
                  new Date().toISOString().split('T')[0]
                }`,
              },
              excelOptions: {
                fileName: `User_Index_Fund_Report_Mutual_Fund${groupByUser ? '_Grouped_By_User' : ''}_${
                  new Date().toISOString().split('T')[0]
                }`,
              },
            }}
          />
        </TabPanel>
        <TabPanel value="2">
          <Typography>
            <Table
              key={`etf-${groupByUser}`}
              ref={tableRef}
              columns={columns}
              fetchItems={fetchUserIndexFundReport}
              fetchItemsCustomParams={{
                category: 'ETF',
                groupByUser,
              }}
              rows={userIndexFundReport}
              loading={isLoading}
              rowCount={totalUserIndexFundReport}
              getRowId={(row) => `${row['index fund id']}-${row['user id'] || 'all'}`}
              disableColumnFilter
              toolbarExportProps={{
                csvOptions: {
                  fileName: `User_Index_Fund_Report_ETF${groupByUser ? '_Grouped_By_User' : ''}_${
                    new Date().toISOString().split('T')[0]
                  }`,
                },
                excelOptions: {
                  fileName: `User_Index_Fund_Report_ETF${groupByUser ? '_Grouped_By_User' : ''}_${
                    new Date().toISOString().split('T')[0]
                  }`,
                },
              }}
            />
          </Typography>
        </TabPanel>
      </TabContext>
    </Box>
  );
};

export default UserIndexFundTab;
